import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Navigation, FileText, CheckCircle2, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtTime, STATUS } from "@/lib/api";
import { TripInfo } from "@/components/TripInfo";
import { StatusButtons } from "@/components/StatusButtons";
import { StatusBadge } from "@/components/StatusBadge";
import { BigButton } from "@/components/BigButton";
import { DocumentViewer } from "@/components/DocumentViewer";
import { t } from "@/i18n";

const History = ({ trip }) => (
  <section className="nv-card p-5" data-testid="trip-history">
    <p className="nv-eyebrow flex items-center gap-2"><Radio className="h-4 w-4 text-[#10b981]" /> {t("Aggiornamenti inviati al vettore")}</p>
    <ol className="mt-4 space-y-4 border-l-2 border-[#243047] ml-2">
      {[...trip.history].reverse().map((h, i) => (
        <li key={i} className="pl-5 relative">
          <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full" style={{ background: STATUS[h.status].color }} />
          <p className="font-semibold">{t(h.label)}</p>
          <p className="text-sm text-slate-400">{fmtTime(h.at)}{h.lat ? ` · ${t("posizione")} ${h.lat.toFixed(3)}, ${h.lng.toFixed(3)}` : ""}</p>
        </li>
      ))}
    </ol>
  </section>
);

export default function TripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [trip, setTrip] = useState(null);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [id]);

  if (!trip) return <div className="p-6 text-slate-400">{t("Caricamento…")}</div>;

  const accept = async () => {
    const { data } = await api.post(`/trips/${trip.id}/accept`);
    setTrip(data);
    toast.success(t("Viaggio accettato"));
  };

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-8 space-y-6">
      <button data-testid="back-btn" onClick={() => nav("/")} className="nv-press flex items-center gap-2 text-lg font-semibold text-slate-300 min-h-[56px]">
        <ArrowLeft className="h-6 w-6" /> {t("Torna ai viaggi")}
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase" data-testid="trip-title">{t("Viaggio {code}", { code: trip.code })}</h1>
        <StatusBadge status={trip.status} testId="trip-status-badge" />
      </div>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <section className="nv-card p-5">
            <p className="nv-eyebrow">{t("Informazioni dal vettore")} · {trip.carrier.name} · {trip.carrier.phone}</p>
            <TripInfo trip={trip} full />
          </section>
          <section className="nv-card p-5">
            <p className="nv-eyebrow mb-3">{t("Stato del viaggio")}</p>
            {trip.status === "assegnato"
              ? <BigButton testId="detail-accept-btn" icon={CheckCircle2} color="#ef0298" label={t("Accetta viaggio")} sub={t("Conferma al vettore")} onClick={accept} />
              : <StatusButtons trip={trip} onChange={setTrip} />}
          </section>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <BigButton testId="detail-navigate-btn" icon={Navigation} color="#4accff" label={t("Avvia navigatore")} sub={t("Percorso per il tuo mezzo")} onClick={() => nav(`/navigatore?trip=${trip.id}`)} />
          <section className="nv-card p-5 space-y-3" id="documenti" data-testid="trip-documents">
            <p className="nv-eyebrow">{t("Archivio digitale")}</p>
            {trip.documents.map((d) => (
              <BigButton key={d.type} testId={`open-doc-${d.type}`} icon={FileText} variant="outline" color={d.signed_at ? "#10b981" : "#ffffff"}
                label={d.type} sub={`${d.number} · ${t(d.signed_at ? "firmato" : "da firmare")}`} onClick={() => setDoc(d.type)} />
            ))}
          </section>
          <History trip={trip} />
        </div>
      </div>
      <DocumentViewer trip={trip} type={doc} onClose={() => setDoc(null)} onSigned={setTrip} />
    </div>
  );
}
